import React, { useEffect, useState, useCallback } from 'react';
import { Survey } from 'survey-react-ui';
import { Model } from 'survey-core';
import 'survey-core/defaultV2.min.css';
import { SimpleFlowStep, SimpleSurveyData } from '../types/simpleSurvey';
import { RoyalGoldTheme } from '../themes/royalGoldTheme';
import { SimpleIntroScreen } from './customSteps/SimpleIntroScreen';
import { BMICalculationScreen } from './customSteps/BMICalculationScreen';
import { LifestyleCategoryIntro } from './customSteps/LifestyleCategoryIntro';
import { LifestyleIntroSlide } from './customSteps/LifestyleIntroSlide';
import { SleepIntroSlide } from './customSteps/SleepIntroSlide';
import { BodyWeightIntroSlide } from './customSteps/BodyWeightIntroSlide';
import { NutritionIntroSlide } from './customSteps/NutritionIntroSlide';
import { PhysicalActivityIntroSlide } from './customSteps/PhysicalActivityIntroSlide';
import { FeelingsIntroSlide } from './customSteps/FeelingsIntroSlide';
import { AddictionsIntroSlide } from './customSteps/AddictionsIntroSlide';
import { SleepAssessmentResults } from './customSteps/SleepAssessmentResults';
import { FeelingsAssessmentResults } from './customSteps/FeelingsAssessmentResults';
import { AddictionsAssessmentResults } from './customSteps/AddictionsAssessmentResults';
import { OverallResults } from './customSteps/OverallResults';
import { BodyWeightAssessmentResults } from './customSteps/BodyWeightAssessmentResults';
import { NutritionAssessmentResults } from './customSteps/NutritionAssessmentResults';
import { PhysicalActivityAssessmentResults } from './customSteps/PhysicalActivityAssessmentResults';``
import { page1 } from '../data/page1';
import { noPage } from '../data/noPage';
import { page2 } from '../data/page2';
import { page3 } from '../data/page3';
import { page4 } from '../data/page4';
import { page5 } from '../data/page5';
import { page6 } from '../data/page6';
import { page7 } from '../data/page7';
import { page8 } from '../data/page8';
import { page9 } from '../data/page9';
import { page10 } from '../data/page10';
import { page11 } from '../data/page11';
import { page12 } from '../data/page12';
import { page13 } from '../data/page13';

interface SimpleQuestionnaireProps {
  surveyJson?: any; // Optional, we'll use default if not provided
}

/**
 * Simplified questionnaire component with localStorage and custom steps
 */
export const SimpleQuestionnaire: React.FC<SimpleQuestionnaireProps> = ({ surveyJson }) => {
  const [survey, setSurvey] = useState<Model | null>(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [flowSteps, setFlowSteps] = useState<SimpleFlowStep[]>([]);
  const [isComplete, setIsComplete] = useState(false);
  const [currentTheme] = useState('royal-gold');
  const STORAGE_KEY = `survey_lifestyle_questionnaire`;

  // Save to localStorage
  const saveToLocalStorage = useCallback((answers: any, step: number) => {
    const data: SimpleSurveyData = {
      answers,
      currentStep: step,
      lastUpdated: Date.now()
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  }, [STORAGE_KEY]);

  // Handle survey completion
  const handleSurveyComplete = useCallback(async () => {
    // Send data to webhook before clearing
    if (survey?.data) {
      try {
        // Prepare the data payload
        const payload = {
          answers: survey.data,
          completedAt: new Date().toISOString(),
          surveyId: 'lifestyle-questionnaire'
        };

        // Send to webhook with proper CORS handling
        await fetch('https://hook.eu2.make.com/3r96r4pq14goupahk50tjpot3ynqc14o', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
          mode: 'no-cors' // This bypasses CORS but you won't get response data
        });

        console.log('Survey data sent to webhook successfully');
      } catch (error) {
        console.error('Failed to send survey data to webhook:', error);
        // Continue with completion even if webhook fails
      }
    }

    // Clear localStorage
    localStorage.removeItem(STORAGE_KEY);
    setIsComplete(true);
  }, [STORAGE_KEY, survey]);

  // Initialize survey and flow
  useEffect(() => {
    // Default survey JSON if not provided
    const defaultSurveyJson = surveyJson || {
      pages: [
        page1,
        noPage,   
        page2,
        page3,
        page4,
        page5,
        page6,
        page7,
        page8,
        page9,
        page10,
        page11,
        page12,
        page13
      ],
      showProgressBar: false,
      showQuestionNumbers: false,
      showCompletedPage: false,
      completeText: 'Finish',
      pageNextText: 'Next',
      pagePrevText: 'Previous'
    };
    
    // Create survey model
    const surveyModel = new Model(defaultSurveyJson);
    
    // Apply the royal gold theme
    surveyModel.applyTheme(RoyalGoldTheme);
    
    // Hide SurveyJS navigation since we're using custom navigation
    surveyModel.showNavigationButtons = false;
    
    // Build explicit flow sequence
    const steps: SimpleFlowStep[] = [];
    let index = 0;
    
    // Define the precise flow sequence
    // 1. Intro screen
    steps.push({
      type: 'custom',
      id: 'intro',
      index: index++,
      title: 'Welcome'
    });

    // Custom intro for Lifestyle Questionnaire
    steps.push({
      type: 'custom',
      id: 'lifestyle-intro',
      index: index++,
      title: 'Lifestyle Section'
    });
    
    // 2. Privacy Policy (page1 in survey definition, but page_0 in SurveyJS)
    if (surveyModel.pages[0]) {
      steps.push({
        type: 'survey',
        id: 'page_0',
        index: index++,
        title: surveyModel.pages[0].title || 'Privacy Policy'
      });
    }
    
    // 3. No consent page (conditionally shown in UI)
    if (surveyModel.pages[1]) {
      steps.push({
        type: 'survey',
        id: 'page_1',
        index: index++,
        title: surveyModel.pages[1].title || 'No consent'
      });
    }
    
    // 4. Gender and Age (page2 in definition, page_2 in SurveyJS)
    if (surveyModel.pages[2]) {
      steps.push({
        type: 'survey',
        id: 'page_2',
        index: index++,
        title: surveyModel.pages[2].title || 'Basic Information'
      });
    }
    
    // 5. Weight and Height (page3 in definition, page_3 in SurveyJS)
    if (surveyModel.pages[3]) {
      steps.push({
        type: 'survey',
        id: 'page_3',
        index: index++,
        title: surveyModel.pages[3].title || 'Physical Measurements'
      });
    }
    
    // 6. BMI Calculation Screen (after weight/height)
    steps.push({
      type: 'custom',
      id: 'bmi-calculation',
      index: index++,
      title: 'BMI Results'
    });
    
    // 7. Lifestyle Category Intro
    steps.push({
      type: 'custom',
      id: 'lifestyle-slide',
      index: index++,
      title: 'Lifestyle'
    });
    
    // 8. Peer Health Rating (page4 in definition, page_4 in SurveyJS)
    if (surveyModel.pages[4]) {
      steps.push({
        type: 'survey',
        id: 'page_4',
        index: index++,
        title: surveyModel.pages[4].title || 'Your Lifestyle'
      });
    }

    steps.push({
      type: 'custom',
      id: 'sleep-slide',
      index: index++,
      title: 'Sleep'
    });

    if (surveyModel.pages[5]) {
      steps.push({
        type: 'survey',
        id: 'page_5',
        index: index++,
        title: surveyModel.pages[5].title || 'Sleep'
      });
    }

    steps.push({
      type: 'custom',
      id: 'sleep-assessment-results',
      index: index++,
      title: 'Sleep Assessment Results'
    });

    steps.push({
      type: 'custom',
      id: 'body-weight-slide',
      index: index++,
      title: 'Body shape and weight'
    });

    if (surveyModel.pages[6]) {
      steps.push({
        type: 'survey',
        id: 'page_6',
        index: index++,
        title: surveyModel.pages[6].title || 'Body shape and weight'
      });
    }

    steps.push({
      type: 'custom',
      id: 'body-weight-assessment-results',
      index: index++,
      title: 'Body Weight Assessment Results'
    });

    steps.push({
      type: 'custom',
      id: 'nutrition-slide',
      index: index++,
      title: 'Nutrition'
    });

    if (surveyModel.pages[7]) {
      steps.push({
        type: 'survey',
        id: 'page_7',
        index: index++,
        title: surveyModel.pages[7].title || 'Nutrition'
      });
    }

    steps.push({
      type: 'custom',
      id: 'nutrition-assessment-results',
      index: index++,
      title: 'Nutrition Assessment Results'
    });

    steps.push({
      type: 'custom',
      id: 'physical-activity-slide',
      index: index++,
      title: 'Physical Activity'
    });
    
    if (surveyModel.pages[8]) {
      steps.push({
        type: 'survey',
        id: 'page_8',
        index: index++,
        title: surveyModel.pages[8].title || 'Physical Activity'
      });
    }

    steps.push({
      type: 'custom',
      id: 'physical-activity-assessment-results',
      index: index++,
      title: 'Physical Activity Assessment Results'
    });

    steps.push({
      type: 'custom',
      id: 'feelings-slide',
      index: index++,
      title: 'The way you feel'
    });

    if (surveyModel.pages[9]) {
      steps.push({
        type: 'survey',
        id: 'page_9',
        index: index++,
        title: surveyModel.pages[9].title || 'The way you feel'
      });
    }

    if (surveyModel.pages[10]) {
      steps.push({
        type: 'survey',
        id: 'page_10',
        index: index++,
        title: surveyModel.pages[10].title || 'The way you feel'
      });
    }

    steps.push({
      type: 'custom',
      id: 'feelings-assessment-results',
      index: index++,
      title: 'Feelings Assessment Results'
    });

    steps.push({
      type: 'custom',
      id: 'addictions-slide',
      index: index++,
      title: 'Addictions'
    });

    if (surveyModel.pages[11]) {
      steps.push({
        type: 'survey',
        id: 'page_11',
        index: index++,
        title: surveyModel.pages[11].title || 'Addictions'
      });
    }

    steps.push({
      type: 'custom',
      id: 'addictions-assessment-results',
      index: index++,
      title: 'Addictions Assessment Results'
    });

    if (surveyModel.pages[12]) {
      steps.push({
        type: 'survey',
        id: 'page_12',
        index: index++,
        title: surveyModel.pages[12].title || 'Change'
      });
    }

    if (surveyModel.pages[13]) {
      steps.push({
        type: 'survey',
        id: 'page_13',
        index: index++,
        title: surveyModel.pages[13].title || 'Contact'
      });
    }

    steps.push({
      type: 'custom',
      id: 'overall-results',
      index: index++,
      title: 'Overall Results'
    });

    // Load saved data from localStorage
    const savedData = localStorage.getItem(STORAGE_KEY);
    let initialStep = 0;
    if (savedData) {
      const parsed: SimpleSurveyData = JSON.parse(savedData);
      surveyModel.data = parsed.answers;
      initialStep = parsed.currentStep;
      
      // If we're on a survey page, set the survey to the correct page
      const stepInfo = steps[initialStep];
      if (stepInfo && stepInfo.type === 'survey') {
        const pageIndex = parseInt(stepInfo.id.replace('page_', ''));
        surveyModel.currentPageNo = pageIndex;
      }
    }

    setSurvey(surveyModel);
    setFlowSteps(steps);
    setCurrentStep(initialStep);
  }, [surveyJson, STORAGE_KEY]);

  // Set up survey event handlers
  useEffect(() => {
    if (!survey || !flowSteps.length) return;

    // Auto-save to localStorage on changes
    const valueChangedHandler = () => {
      saveToLocalStorage(survey.data, currentStep);
    };

    // Handle page changing (before the page actually changes) - ONLY for SurveyJS internal navigation
    const pageChangingHandler = (_sender: any, options: any) => {
      // We only use this to track SurveyJS internal page changes
      // Custom step navigation is handled by handleNext/handlePrevious
      console.log(`SurveyJS page changing from ${options.oldCurrentPage.name} to ${options.newCurrentPage.name}`);
    };

    // Track page changes within survey
    const pageChangedHandler = () => {
      // Find which step corresponds to this survey page
      const pageIndex = survey.currentPageNo;
      const stepIndex = flowSteps.findIndex(s => s.type === 'survey' && s.id === `page_${pageIndex}`);
      if (stepIndex >= 0 && stepIndex !== currentStep) {
        setCurrentStep(stepIndex);
        saveToLocalStorage(survey.data, stepIndex);
      }
    };

    // Handle survey completion
    const completeHandler = () => {
      handleSurveyComplete();
    };

    survey.onValueChanged.add(valueChangedHandler);
    survey.onCurrentPageChanging.add(pageChangingHandler);
    survey.onCurrentPageChanged.add(pageChangedHandler);
    survey.onComplete.add(completeHandler);

    // Cleanup
    return () => {
      survey.onValueChanged.remove(valueChangedHandler);
      survey.onCurrentPageChanging.remove(pageChangingHandler);
      survey.onCurrentPageChanged.remove(pageChangedHandler);
      survey.onComplete.remove(completeHandler);
    };
  }, [survey, currentStep, flowSteps, saveToLocalStorage, handleSurveyComplete]);

  // Handle start over
  const handleStartOver = useCallback(() => {
    if (confirm('Are you sure you want to start over? This will clear all your answers.')) {
      localStorage.removeItem(STORAGE_KEY);
      window.location.reload();
    }
  }, [STORAGE_KEY]);

  // Handle next step
  const handleNext = useCallback(() => {
    const currentFlowStep = flowSteps[currentStep];
    
    // Special handling for Privacy Policy page
    if (currentFlowStep?.id === 'page_0' && survey) {
      // Check privacy policy consent
      const privacyAccepted = survey.data?.privacyPolicy;
      
      if (privacyAccepted === false) {
        // Go to no consent page (page_1)
        const noConsentIndex = flowSteps.findIndex(s => s.id === 'page_1');
        if (noConsentIndex >= 0) {
          setCurrentStep(noConsentIndex);
          survey.currentPageNo = 1;
          saveToLocalStorage(survey.data, noConsentIndex);
        }
        return;
      } else if (privacyAccepted === true) {
        // Skip no consent page, go to Gender/Age page (page_2)
        const genderPageIndex = flowSteps.findIndex(s => s.id === 'page_2');
        if (genderPageIndex >= 0) {
          setCurrentStep(genderPageIndex);
          survey.currentPageNo = 2;
          saveToLocalStorage(survey.data, genderPageIndex);
        }
        return;
      }
      // If privacyAccepted is undefined (not answered yet), don't navigate
      return;
    }
    
    // Special handling for no consent page - it's a dead end
    if (currentFlowStep?.id === 'page_1') {
      // No next action from no consent page
      return;
    }
    
    // Regular navigation for all other steps
    if (currentStep < flowSteps.length - 1) {
      let nextStep = currentStep + 1;
      
      // Skip no consent page in normal flow
      if (flowSteps[nextStep]?.id === 'page_1') {
        nextStep = currentStep + 2; // Skip to next after no consent
      }
      
      const nextFlowStep = flowSteps[nextStep];
      
      // If next is a survey page, set the survey to that page
      if (nextFlowStep?.type === 'survey' && survey) {
        const pageIndex = parseInt(nextFlowStep.id.replace('page_', ''));
        survey.currentPageNo = pageIndex;
      }
      
      setCurrentStep(nextStep);
      if (survey) {
        saveToLocalStorage(survey.data, nextStep);
      }
    } else {
      // Last step - complete
      handleSurveyComplete();
    }
  }, [currentStep, flowSteps, survey, saveToLocalStorage, handleSurveyComplete]);

  // Handle previous step
  const handlePrevious = useCallback(() => {
    const currentFlowStep = flowSteps[currentStep];
    
    // Special handling for no consent page - can go back to privacy policy
    if (currentFlowStep?.id === 'page_1') {
      const privacyPageIndex = flowSteps.findIndex(s => s.id === 'page_0');
      if (privacyPageIndex >= 0 && survey) {
        setCurrentStep(privacyPageIndex);
        survey.currentPageNo = 0;
        saveToLocalStorage(survey.data, privacyPageIndex);
      }
      return;
    }
    
    // Special handling for Gender/Age page - should go back to privacy policy
    if (currentFlowStep?.id === 'page_2') {
      const privacyPageIndex = flowSteps.findIndex(s => s.id === 'page_0');
      if (privacyPageIndex >= 0 && survey) {
        setCurrentStep(privacyPageIndex);
        survey.currentPageNo = 0;
        saveToLocalStorage(survey.data, privacyPageIndex);
      }
      return;
    }
    
    // Regular navigation for all other steps
    if (currentStep > 0) {
      let prevStep = currentStep - 1;
      
      // Skip no consent page when going back in normal flow
      if (flowSteps[prevStep]?.id === 'page_1') {
        prevStep = currentStep - 2; // Skip no consent page
      }
      
      const prevFlowStep = flowSteps[prevStep];
      
      // If previous is a survey page, set the survey to that page
      if (prevFlowStep?.type === 'survey' && survey) {
        const pageIndex = parseInt(prevFlowStep.id.replace('page_', ''));
        survey.currentPageNo = pageIndex;
      }
      
      setCurrentStep(prevStep);
      if (survey) {
        saveToLocalStorage(survey.data, prevStep);
      }
    }
  }, [currentStep, flowSteps, survey, saveToLocalStorage]);

  // Show completion screen
  if (isComplete) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-6 text-center">
          <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
            <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className="text-lg font-semibold text-gray-900 mb-2">Survey Complete!</h2>
          <p className="text-gray-600 mb-4 text-sm">
            Thank you for completing the survey. Your responses have been recorded.
          </p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors duration-200 text-sm"
          >
            Start New Survey
          </button>
        </div>
      </div>
    );
  }

  // Get current step
  const currentFlowStep = flowSteps[currentStep];
  
  // Show custom step if current step is custom
  if (currentFlowStep && currentFlowStep.type === 'custom') {
    // Map step IDs to their components
    const customComponents: Record<string, React.ComponentType<any>> = {
      'intro': SimpleIntroScreen,
      'bmi-calculation': BMICalculationScreen,
      'lifestyle-intro': LifestyleCategoryIntro,
      'lifestyle-slide': LifestyleIntroSlide,
      'sleep-slide': SleepIntroSlide,
      'sleep-assessment-results': SleepAssessmentResults,
      'body-weight-slide': BodyWeightIntroSlide,
      'body-weight-assessment-results': BodyWeightAssessmentResults,
      'nutrition-slide': NutritionIntroSlide,
      'nutrition-assessment-results': NutritionAssessmentResults,
      'physical-activity-slide': PhysicalActivityIntroSlide,
      'physical-activity-assessment-results': PhysicalActivityAssessmentResults,
      'feelings-slide': FeelingsIntroSlide,
      'feelings-assessment-results': FeelingsAssessmentResults,
      'addictions-slide': AddictionsIntroSlide,
      'addictions-assessment-results': AddictionsAssessmentResults,
      'overall-results': OverallResults,
    };
    
    const Component = customComponents[currentFlowStep.id];
    if (Component) {
      return (
        <div className="min-h-screen bg-gray-50">
          <Component
            onNext={handleNext}
            onBack={handlePrevious}
            onComplete={handleNext}
            onReset={handleStartOver}
            isFirst={currentStep === 0}
            isLast={currentStep === flowSteps.length - 1}
            currentStep={currentStep}
            totalSteps={flowSteps.length}
            surveyData={{
              surveyId: 'lifestyle-questionnaire',
              schemaVersion: '1.0.0',
              answers: survey?.data || {},
              currentPage: 0,
              currentStep,
              customData: {},
              lastUpdated: Date.now()
            }}
            context={{
              surveyAnswers: survey?.data || {},
              currentPage: 0,
              currentStep,
              urlParams: new URLSearchParams(window.location.search),
              featureFlags: {},
              customData: {}
            }}
          />
        </div>
      );
    }
  }

  // Show SurveyJS component for survey pages
  if (survey && currentFlowStep && currentFlowStep.type === 'survey') {
    return (
      <div className="min-h-screen bg-gray-50 p-4">
        <div className="max-w-4xl mx-auto">
          {/* Header with restart and theme switcher */}
          <div className="mb-4 flex justify-end items-center">
            {/* <ThemeSwitcher 
              survey={survey} 
              onThemeChange={(themeId) => setCurrentTheme(themeId)}
            /> */}
            <button
              onClick={handleStartOver}
              className="px-4 py-2 text-sm text-red-700 hover:text-red-800 font-medium transition-colors duration-200"
            >
              Restart
            </button>
          </div>

          {/* Survey component - key forces re-render on theme change */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <Survey key={currentTheme} model={survey} />
          </div>

          {/* Custom Navigation - Always use our own navigation */}
          <div className="mt-6 flex justify-between items-center">
            <button
              onClick={handlePrevious}
              disabled={currentStep === 0 || currentFlowStep?.id === 'page_1'}
              className="px-6 py-2 bg-gray-200 text-gray-700 font-medium rounded-lg hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
            >
              Previous
            </button>
            
            <button
              onClick={handleNext}
              disabled={currentFlowStep?.id === 'page_1'}
              className="px-6 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
            >
              {currentStep === flowSteps.length - 1 ? 'Finish' : 'Next'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Loading state
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
        <p className="text-gray-600 text-sm">Loading...</p>
      </div>
    </div>
  );
};